import { useCallback, useEffect, useRef, useState } from "react";

import ApiClientAsync from "../../api/ApiClientAsync";
import config from "../../config";
import { fetchMediaMonitorPage, resolveMarketId } from "../StockPage";

export function useMediaMonitorPagination({
  market,
  ticker,
  locale,
  authToken,
  marketTranslations,
  pageSize = config.recommendationsToFetch
}) {
  const [data, setData] = useState({ messages: [], is_last_page: true });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const dataRef = useRef(data);
  dataRef.current = data;

  const resolvedMarket = resolveMarketId(marketTranslations, market);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const client = new ApiClientAsync(locale, authToken);
        const page = await fetchMediaMonitorPage(
          client,
          resolvedMarket,
          ticker,
          0,
          pageSize
        );
        if (!cancelled) {
          setData(page);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setData({ messages: [], is_last_page: true });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [authToken, locale, pageSize, resolvedMarket, ticker]);

  const loadMoreMediaMonitor = useCallback(async () => {
    if (loadingMore) {
      return;
    }

    const offset = dataRef.current.messages?.length || 0;

    setLoadingMore(true);
    try {
      const client = new ApiClientAsync(locale, authToken);
      const page = await fetchMediaMonitorPage(
        client,
        resolvedMarket,
        ticker,
        offset,
        pageSize
      );
      setData(prev => ({
        messages: [...(prev.messages || []), ...(page.messages || [])],
        is_last_page: page.is_last_page
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingMore(false);
    }
  }, [authToken, loadingMore, locale, pageSize, resolvedMarket, ticker]);

  return {
    data,
    loading,
    loadingMore,
    loadMoreMediaMonitor,
    resolvedMarket
  };
}
